// src/utils/firebase.js
import { 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ユニークなユーザーIDを生成または取得
export const getUserId = () => {
  let userId = localStorage.getItem('userId');
  if (!userId) {
    userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('userId', userId);
  }
  return userId;
};

// ★ 追加：新しい userId を発行して上書きする関数
export const resetUserId = () => {
  const newUserId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  localStorage.setItem('userId', newUserId);
  return newUserId;
};

// セッションIDを生成
export const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// ユーザープロファイルをFirestoreに保存
export const saveUserProfile = async (profileData) => {
  try {
    const userId = getUserId();
    const userDocRef = doc(db, 'users', userId);
    
    await setDoc(userDocRef, {
      ...profileData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true }); // 既存データがある場合はマージ
    
    console.log('User profile saved successfully');
    return userId;
  } catch (error) {
    console.error('Error saving user profile:', error);
    throw error;
  }
};

// ガイドセッションを開始
export const startGuideSession = async (location = null) => {
  try {
    const userId = getUserId();
    const sessionId = generateSessionId();
    
    const sessionData = {
      startTime: serverTimestamp(),
      endTime: null,
      status: 'active'
    };

    // 開始位置がある場合は追加
    if (location) {
      sessionData.startLocation = {
        lat: location.lat,
        lng: location.lng,
        timestamp: serverTimestamp()
      };
    }
    
    const sessionDocRef = doc(db, 'users', userId, 'sessions', sessionId);
    await setDoc(sessionDocRef, sessionData);
    
    console.log('Guide session started:', sessionId);
    return sessionId;
  } catch (error) {
    console.error('Error starting guide session:', error);
    throw error;
  }
};

// ガイドセッションを終了
export const endGuideSession = async (sessionId, location = null) => {
  try {
    const userId = getUserId();
    
    const endData = {
      endTime: serverTimestamp(),
      status: 'completed',
      updatedAt: serverTimestamp()
    };

    // 終了位置がある場合は追加
    if (location) {
      endData.endLocation = {
        lat: location.lat,
        lng: location.lng,
        timestamp: serverTimestamp()
      };
    }
    
    const sessionDocRef = doc(db, 'users', userId, 'sessions', sessionId);
    await setDoc(sessionDocRef, endData, { merge: true });
    
    console.log('Guide session ended:', sessionId);
  } catch (error) {
    console.error('Error ending guide session:', error);
    throw error;
  }
};

// 位置情報をFirestoreに保存
export const saveLocation = async (sessionId, location) => {
  try {
    const userId = getUserId();
    
    const locationData = {
      lat: location.lat,
      lng: location.lng,
      timestamp: serverTimestamp(),
      accuracy: location.accuracy || null,
      // タイムスタンプをIDとしても使用（ソート用）
      timestampId: Date.now()
    };
    
    // locations サブコレクションに追加
    const locationsRef = collection(db, 'users', userId, 'sessions', sessionId, 'locations');
    await addDoc(locationsRef, locationData);
    
    console.log('Location saved successfully');
  } catch (error) {
    console.error('Error saving location:', error);
    // 位置情報の保存エラーは重要ではないのでthrowしない
  }
};

// ★ 追加：満足度アンケートをFirestoreに保存（saveUserProfileと同じ users/{userId} への保存パターン）
export const saveSatisfactionSurvey = async (surveyData) => {
  try {
    const userId = getUserId();
    const userDocRef = doc(db, 'users', userId);

    await setDoc(userDocRef, {
      satisfactionSurvey: {
        ...surveyData,
        answeredAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    }, { merge: true }); // 既存のプロフィール情報は上書きしない

    console.log('Satisfaction survey saved successfully');
    return userId;
  } catch (error) {
    console.error('Error saving satisfaction survey:', error);
    throw error;
  }
};
export const saveAudioPlay = async (sessionId, spotId) => {
  try {
    const userId = getUserId();

    const audioPlayData = {
      spotId,
      timestamp: serverTimestamp(),
      // タイムスタンプをIDとしても使用（ソート用）
      timestampId: Date.now()
    };

    // audioPlays サブコレクションに追加
    const audioPlaysRef = collection(db, 'users', userId, 'sessions', sessionId, 'audioPlays');
    await addDoc(audioPlaysRef, audioPlayData);

    console.log('Audio play saved successfully');
  } catch (error) {
    console.error('Error saving audio play:', error);
    // 再生ログの保存エラーは重要ではないのでthrowしない
  }
};